import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Save, Check, X, Megaphone, CalendarPlus, BookMarked, HandCoins, Share2, ShieldAlert, UserPlus, Download } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";

const D = { navy: "#1e2130", surface: "#292d44", coral: "#f76359", cream: "#faf6f0" };
const TABS = [
  ["settings", "Settings"], ["members", "Members"], ["engage", "Engage"],
  ["giving", "Giving"], ["growth", "Growth"], ["safety", "Safety"],
];
const CHECKS = [
  ["public_venue", "Meets at a verified public venue (cafe, library, park, community center)"],
  ["no_private", "Never a home or private address"],
  ["three_plus", "Expecting 3 or more people"],
  ["us_only", "Located in the United States"],
  ["accessible", "Venue is accessible to all abilities"],
];

const inp = "w-full rounded-xl border border-[#faf6f0]/15 bg-[#1e2130] px-3 py-2 text-[#faf6f0] placeholder-[#faf6f0]/40 focus:border-[#f76359] focus:outline-none";
const btn = "inline-flex items-center gap-2 rounded-full bg-[#f76359] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#e2554c]";
const ghost = "inline-flex items-center gap-2 rounded-full border border-[#faf6f0]/25 px-4 py-2 text-sm font-semibold text-[#faf6f0] hover:bg-[#faf6f0]/10";

export default function CuratorGroup() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("settings");
  const [g, setG] = useState(null);
  const origin = window.location.origin;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/studies/${id}`); setG(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); nav("/curator"); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  if (!g) return <div style={{ background: D.navy }} className="min-h-screen p-10 text-center text-[#faf6f0]/70">Loading…</div>;

  return (
    <div style={{ background: D.navy, color: D.cream }} className="min-h-screen">
      <div className="mx-auto max-w-5xl px-5 py-6 sm:px-8">
        <button onClick={() => nav("/curator")} className="mb-3 flex items-center gap-1 text-sm text-[#faf6f0]/60 hover:text-white"><ChevronLeft size={16} /> Back office</button>
        <h1 className="text-2xl font-extrabold tracking-tight text-[#faf6f0] sm:text-3xl">{g.title}</h1>
        <p className="text-sm text-[#faf6f0]/60">{g.venue.name} · {g.schedule.day_name}s {g.schedule.start_time} · <a className="text-[#f76359] hover:underline" href={`/study/${id}`}>public front door →</a></p>

        <div className="mt-5 flex flex-wrap gap-1.5 border-b border-[#faf6f0]/10 pb-2">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`ctab-${v}`}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === v ? "bg-[#f76359] text-white" : "text-[#faf6f0]/70 hover:text-white"}`}>{l}</button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "settings" && <Settings id={id} g={g} reload={load} />}
          {tab === "members" && <Members id={id} />}
          {tab === "engage" && <Engage id={id} origin={origin} />}
          {tab === "giving" && <Giving id={id} g={g} origin={origin} reload={load} />}
          {tab === "growth" && <Growth id={id} origin={origin} />}
          {tab === "safety" && <Safety id={id} />}
        </div>
      </div>
    </div>
  );
}

const Card = ({ children, className = "" }) => <div className={`rounded-[18px] p-5 ${className}`} style={{ background: D.surface }}>{children}</div>;
const Label = ({ children }) => <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-[#faf6f0]/60">{children}</label>;

function Settings({ id, g, reload }) {
  const [f, setF] = useState({ title: g.title, description: g.description || "", photo: g.photo || "", capacity: g.capacity || "", requires_approval: !!g.requires_approval });
  const [checklist, setChecklist] = useState(g.checklist || {});
  const save = async () => {
    try {
      await api.patch(`/curator/groups/${id}`, {
        title: f.title, description: f.description, photo: f.photo || null,
        capacity: f.capacity ? Number(f.capacity) : null, requires_approval: f.requires_approval, checklist,
      });
      toast.success("Saved"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4">
      <Card>
        <Label>Group name</Label>
        <input className={inp} data-testid="set-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <div className="mt-3"><Label>Description (public front door)</Label>
          <textarea className={inp} rows={3} data-testid="set-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
        <div className="mt-3"><Label>Photo URL</Label>
          <input className={inp} data-testid="set-photo" value={f.photo} onChange={(e) => setF({ ...f, photo: e.target.value })} placeholder="https://…" /></div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div><Label>Capacity (optional)</Label><input type="number" className={inp} data-testid="set-capacity" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} /></div>
          <label className="mt-6 flex items-center gap-2 text-sm text-[#faf6f0]/85"><input type="checkbox" data-testid="set-approval" checked={f.requires_approval} onChange={(e) => setF({ ...f, requires_approval: e.target.checked })} /> Approve each join request</label>
        </div>
      </Card>
      <Card>
        <div className="mb-2 font-bold text-[#faf6f0]">Public-venue safety checklist</div>
        {CHECKS.map(([k, label]) => (
          <label key={k} className="flex items-start gap-2 py-1.5 text-sm text-[#faf6f0]/85">
            <input type="checkbox" data-testid={`check-${k}`} checked={!!checklist[k]} onChange={(e) => setChecklist({ ...checklist, [k]: e.target.checked })} /> {label}
          </label>
        ))}
      </Card>
      <button className={btn} onClick={save} data-testid="settings-save"><Save size={16} /> Save settings</button>
    </div>
  );
}

function Members({ id }) {
  const [r, setR] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get(`/curator/groups/${id}/roster`); setR(data); }, [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (uid, kind) => { await api.post(`/curator/groups/${id}/members/${uid}/${kind}`); toast.success(kind === "approve" ? "Approved" : "Declined"); load(); };
  const [coc, setCoc] = useState("");
  const addCoc = async () => { try { await api.post(`/curator/groups/${id}/cocurators`, { email: coc }); setCoc(""); toast.success("Co-curator added"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (!r) return <p className="text-[#faf6f0]/60">Loading…</p>;
  const Section = ({ title, rows, actions }) => (
    <Card className="mb-4">
      <div className="mb-2 font-bold text-[#faf6f0]">{title} <span className="text-[#faf6f0]/50">({rows.length})</span></div>
      {rows.length === 0 ? <p className="text-sm text-[#faf6f0]/50">None</p> : rows.map((m) => (
        <div key={m.user_id} className="flex items-center justify-between border-t border-[#faf6f0]/10 py-2 text-sm">
          <span className="text-[#faf6f0]">{m.name} <span className="text-[#faf6f0]/45">· {m.attended} check-ins</span></span>
          {actions && actions(m)}
        </div>
      ))}
    </Card>
  );
  return (
    <div>
      <Section title="Pending approval" rows={r.pending} actions={(m) => (
        <span className="flex gap-2">
          <button data-testid={`approve-${m.user_id}`} onClick={() => act(m.user_id, "approve")} className="rounded-full bg-emerald-500/20 p-1.5 text-emerald-300"><Check size={15} /></button>
          <button data-testid={`decline-${m.user_id}`} onClick={() => act(m.user_id, "decline")} className="rounded-full bg-red-500/20 p-1.5 text-red-300"><X size={15} /></button>
        </span>)} />
      <Section title="Confirmed members" rows={r.confirmed} />
      <Section title="Waitlist" rows={r.waitlist} actions={(m) => (
        <button data-testid={`promote-${m.user_id}`} onClick={() => act(m.user_id, "approve")} className={ghost}>Promote</button>)} />
      <Section title="Co-curators" rows={r.cocurators} />
      <Card>
        <Label>Add a co-curator (their account email)</Label>
        <div className="flex gap-2">
          <input className={inp} data-testid="coc-email" value={coc} onChange={(e) => setCoc(e.target.value)} placeholder="friend@email.com" />
          <button className={btn} onClick={addCoc} data-testid="coc-add"><UserPlus size={15} /> Add</button>
        </div>
      </Card>
    </div>
  );
}

function Engage({ id, origin }) {
  const [text, setText] = useState(""); const [when, setWhen] = useState("");
  const [anns, setAnns] = useState([]); const [links, setLinks] = useState(null);
  const [plan, setPlan] = useState({ name: "Reading plan", items: [{ ref: "", prompt: "" }] });
  const load = useCallback(async () => {
    const [a, l, p] = await Promise.all([
      api.get(`/curator/groups/${id}/announcements`), api.get(`/groups/${id}/calendar-links`), api.get(`/groups/${id}/reading-plan`)]);
    setAnns(a.data.announcements); setLinks(l.data);
    if (p.data.items?.length) setPlan(p.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    try { await api.post(`/curator/groups/${id}/announcements`, { text, send_at: when ? new Date(when).toISOString() : null }); setText(""); setWhen(""); toast.success(when ? "Scheduled" : "Sent to members"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const savePlan = async () => { await api.put(`/curator/groups/${id}/reading-plan`, { name: plan.name, items: plan.items.filter((i) => i.ref) }); toast.success("Reading plan saved"); };
  const setItem = (i, k, v) => setPlan({ ...plan, items: plan.items.map((it, j) => j === i ? { ...it, [k]: v } : it) });
  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-2 flex items-center gap-2 font-bold text-[#faf6f0]"><Megaphone size={17} className="text-[#f76359]" /> Announcements</div>
        <textarea className={inp} rows={2} data-testid="ann-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Message your members…" />
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input type="datetime-local" className={`${inp} max-w-[230px]`} data-testid="ann-when" value={when} onChange={(e) => setWhen(e.target.value)} />
          <button className={btn} onClick={send} data-testid="ann-send">{when ? "Schedule" : "Send now"}</button>
          <span className="text-xs text-[#faf6f0]/50">Leave time empty to send immediately. Reminders auto-send 24h & 1h before.</span>
        </div>
        {anns.map((a) => <div key={a.id} className="mt-2 rounded-lg bg-[#1e2130] p-2 text-sm text-[#faf6f0]/80">{a.text} <span className="text-[#faf6f0]/40">· {a.status} {a.send_at ? new Date(a.send_at).toLocaleString() : ""}</span></div>)}
      </Card>
      <Card>
        <div className="mb-2 flex items-center gap-2 font-bold text-[#faf6f0]"><CalendarPlus size={17} className="text-[#f76359]" /> Calendar invite</div>
        {links && <div className="flex flex-wrap gap-2">
          <a className={btn} href={`${API}${links.ics}`} data-testid="ics-download"><Download size={15} /> Download .ics</a>
          <a className={ghost} href={links.google} target="_blank" rel="noreferrer" data-testid="gcal-link">Add to Google Calendar</a>
        </div>}
      </Card>
      <Card>
        <div className="mb-2 flex items-center gap-2 font-bold text-[#faf6f0]"><BookMarked size={17} className="text-[#f76359]" /> Reading plan & prompts</div>
        {plan.items.map((it, i) => (
          <div key={i} className="mb-2 grid gap-2 sm:grid-cols-2">
            <input className={inp} data-testid={`plan-ref-${i}`} value={it.ref} onChange={(e) => setItem(i, "ref", e.target.value)} placeholder="John 1 or Genesis 1:1-3" />
            <input className={inp} data-testid={`plan-prompt-${i}`} value={it.prompt} onChange={(e) => setItem(i, "prompt", e.target.value)} placeholder="Discussion prompt (opens word view)" />
          </div>
        ))}
        <div className="flex gap-2">
          <button className={ghost} onClick={() => setPlan({ ...plan, items: [...plan.items, { ref: "", prompt: "" }] })}>+ Add day</button>
          <button className={btn} onClick={savePlan} data-testid="plan-save">Save plan</button>
        </div>
      </Card>
    </div>
  );
}

function Giving({ id, g, origin, reload }) {
  const [s, setS] = useState(null); const [goal, setGoal] = useState(g.donation_goal || ""); const [receipts, setReceipts] = useState([]);
  const load = useCallback(async () => {
    const [sum, rec] = await Promise.all([api.get(`/groups/${id}/giving`), api.get(`/curator/groups/${id}/donations`)]);
    setS(sum.data); setReceipts(rec.data.receipts);
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const saveGoal = async () => { await api.patch(`/curator/groups/${id}`, { donation_goal: Number(goal) }); toast.success("Goal set"); reload(); load(); };
  const give = async (amount) => {
    try { const { data } = await api.post(`/groups/${id}/donate`, { amount, origin_url: origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!s) return <p className="text-[#faf6f0]/60">Loading…</p>;
  const pct = s.goal ? Math.min(100, Math.round((s.raised / s.goal) * 100)) : 0;
  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-2 flex items-center gap-2 font-bold text-[#faf6f0]"><HandCoins size={17} className="text-[#f76359]" /> Goal tracker</div>
        <div className="h-3 overflow-hidden rounded-full bg-[#1e2130]"><div style={{ width: `${pct}%`, background: D.coral }} className="h-full" /></div>
        <p className="mt-2 text-sm text-[#faf6f0]/80">${(s.raised / 100).toFixed(2)} raised {s.goal ? `of $${(s.goal / 100).toFixed(0)}` : ""} · {s.count} gifts · est. payout ${(s.payout_estimate / 100).toFixed(2)}</p>
        <div className="mt-2 flex gap-2">
          <input type="number" className={`${inp} max-w-[160px]`} data-testid="goal-input" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Goal $" />
          <button className={ghost} onClick={saveGoal} data-testid="goal-save">Set goal</button>
        </div>
      </Card>
      <Card>
        <div className="mb-2 font-bold text-[#faf6f0]">3-tap giving <span className="text-xs font-normal text-amber-300">· Stripe TEST mode</span></div>
        <div className="flex flex-wrap gap-2">
          {s.tiers.map((t) => <button key={t} className={btn} data-testid={`give-${t}`} onClick={() => give(t)}>Give ${(t / 100).toFixed(0)}</button>)}
          <button className={ghost} data-testid="give-custom" onClick={() => { const v = prompt("Amount in USD"); if (v) give(Math.round(parseFloat(v) * 100)); }}>Custom</button>
        </div>
        <p className="mt-2 text-xs text-[#faf6f0]/50">Test card 4242 4242 4242 4242. No live charges until the owner supplies and approves live keys.</p>
      </Card>
      <Card>
        <div className="mb-2 font-bold text-[#faf6f0]">Receipts & payout summary</div>
        {receipts.length === 0 ? <p className="text-sm text-[#faf6f0]/50">No gifts yet.</p> :
          receipts.map((r, i) => <div key={i} className="flex justify-between border-t border-[#faf6f0]/10 py-1.5 text-sm text-[#faf6f0]/80"><span>{r.name}</span><span>${(r.amount / 100).toFixed(2)}</span></div>)}
      </Card>
    </div>
  );
}

function Growth({ id, origin }) {
  const share = `${origin}/study/${id}`;
  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-2 flex items-center gap-2 font-bold text-[#faf6f0]"><Share2 size={17} className="text-[#f76359]" /> Share link</div>
        <div className="flex gap-2"><input className={inp} readOnly value={share} data-testid="share-link" />
          <button className={ghost} onClick={() => { navigator.clipboard?.writeText(share); toast.success("Copied"); }}>Copy</button></div>
      </Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <div className="mb-2 font-bold text-[#faf6f0]">Printable table QR</div>
          <img src={`${API}/groups/${id}/qrcode.png?origin=${encodeURIComponent(origin)}`} alt="QR code to join group" data-testid="qr-img" className="h-44 w-44 rounded-lg bg-white p-2" />
          <a className={`${ghost} mt-2`} href={`${API}/groups/${id}/qrcode.png?origin=${encodeURIComponent(origin)}`} download>Download QR</a>
        </Card>
        <Card>
          <div className="mb-2 font-bold text-[#faf6f0]">Shareable story image</div>
          <img src={`${API}/groups/${id}/story.png`} alt="Shareable story graphic" data-testid="story-img" className="w-full rounded-lg" />
          <a className={`${ghost} mt-2`} href={`${API}/groups/${id}/story.png`} download>Download story</a>
        </Card>
      </div>
    </div>
  );
}

function Safety({ id }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/curator/groups/${id}/concerns`).then(({ data }) => setItems(data.concerns)); }, [id]);
  return (
    <Card>
      <div className="mb-2 flex items-center gap-2 font-bold text-[#faf6f0]"><ShieldAlert size={17} className="text-[#f76359]" /> Reported concerns</div>
      <p className="mb-3 text-sm text-[#faf6f0]/60">Members can report a concern from the public group page. US public venues only, 3+ people.</p>
      {items.length === 0 ? <p className="text-sm text-[#faf6f0]/50">No concerns reported.</p> :
        items.map((c, i) => <div key={i} className="border-t border-[#faf6f0]/10 py-2 text-sm text-[#faf6f0]/80"><b>{c.reporter}</b>: {c.text}</div>)}
    </Card>
  );
}
